import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { MatchRing } from "@/components/MatchRing";
import { Briefcase, FileText, Send, ArrowRight } from "lucide-react";

export const CandidateDashboard = () => {
  const { user } = useAuth();
  const [resume, setResume] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);

  useEffect(() => {
    api.get("/resume/me").then((r) => setResume(r.data)).catch(() => {});
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
    api.get("/applications/mine").then((r) => setApps(r.data)).catch(() => {});
  }, []);

  const bestScore = apps.reduce((m, a) => Math.max(m, a.match?.overall || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="mono-label">candidate</div>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-2">
        Welcome, {user?.name?.split(" ")[0]}.
      </h1>
      <p className="text-slate-400 mt-2">Track your matches and next moves.</p>

      <div className="grid md:grid-cols-4 gap-4 mt-8">
        <StatCard label="Resume" value={resume ? "Saved" : "Not saved"} to={resume ? "/candidate/resume" : "/candidate/resume"} cta={resume ? "Edit" : "Add resume"} icon={FileText} />
        <StatCard label="Open jobs" value={jobs.length} to="/jobs" cta="Browse" icon={Briefcase} />
        <StatCard label="Applications" value={apps.length} to="/candidate/applications" cta="View" icon={Send} />
        <div className="border border-white/10 bg-[#111827] rounded-xl p-5 flex items-center justify-between">
          <div>
            <div className="mono-label">best match</div>
            <div className="font-display text-2xl mt-2">{bestScore || "—"}</div>
          </div>
          <MatchRing value={bestScore} size={64} stroke={6} />
        </div>
      </div>

      <div className="mt-12">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="mono-label">recommended for you</div>
            <h2 className="font-display text-2xl font-semibold mt-1">Latest openings</h2>
          </div>
          <Link to="/jobs" className="text-sm text-[#06B6D4] hover:underline flex items-center gap-1" data-testid="dash-view-all-jobs">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {jobs.slice(0, 4).map((j) => (
            <Link
              key={j.job_id}
              to={`/jobs/${j.job_id}`}
              className="border border-white/10 bg-[#111827] rounded-xl p-5 hover:border-white/20 hover:-translate-y-0.5 transition-transform block"
              data-testid={`dash-job-${j.job_id}`}
            >
              <div className="flex justify-between items-start gap-4">
                <div>
                  <div className="font-display text-lg font-medium">{j.title}</div>
                  <div className="text-sm text-slate-400 mt-1">{j.company} · {j.location}</div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {j.required_skills.slice(0, 4).map((s) => (
                      <span key={s} className="text-xs px-2 py-0.5 rounded border border-white/10 text-slate-300">{s}</span>
                    ))}
                  </div>
                </div>
                {j.salary_range && (
                  <div className="mono-label text-slate-300 whitespace-nowrap">{j.salary_range}</div>
                )}
              </div>
            </Link>
          ))}
          {jobs.length === 0 && <div className="text-slate-500 text-sm">No jobs available yet.</div>}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, to, cta, icon: Icon }) => (
  <div className="border border-white/10 bg-[#111827] rounded-xl p-5">
    <div className="flex items-center justify-between">
      <div className="mono-label">{label}</div>
      <Icon className="w-4 h-4 text-slate-500" />
    </div>
    <div className="font-display text-2xl mt-3">{value}</div>
    <Link to={to} className="text-sm text-[#06B6D4] hover:underline mt-2 inline-block">{cta}</Link>
  </div>
);
